import React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  DATA_OVERLAY_PRESETS,
  OVERLAY_CATEGORIES
} from '../data-overlays/catalogue';
import {
  MAX_DATA_OVERLAY_SLOTS,
  TREATMENTS,
  slotColour
} from '../data-overlays/slots';
import DataOverlayCriteriaPopover from '../data-overlays/DataOverlayCriteriaPopover';

// TGCLOUD-1663 §11.4 — annotations drawn OVER the pedigree, gathered behind one
// icon on the pedigree panel.
//
// The pedigree-analysis overlay was its own header icon, and the consolidated
// label modal briefly also carried it as a switch. Neither is right on its own:
// a single toggle a clinician flicks on and off while reading a chart should be
// one click away, not behind a configuration modal you open, set and forget —
// but more overlays are coming, and each one cannot become another header icon.
//
// So: one icon, switches inside, and the icon carries a dot while any overlay is
// on so the chart's state is legible without opening it. Overlays are session
// view state and deliberately not preset-backed.
//
// TGCLOUD-1670 adds the data overlays below the analysis switch. Two rules
// follow from Tim's brief that overlays are a CORE pedigree capability:
//
//   1. The icon is never hidden. It used to disappear entirely when no analysis
//      existed, which on a Clinical-Insights-off deployment meant the whole
//      Overlays affordance was gone. Data overlays are ungated, so the icon
//      always renders and the analysis switch is merely one row that may be
//      absent.
//   2. Nothing on this path reads an AI settings flag. The single exception is
//      the analysis row itself, which was always gated that way.

// Each entry reads and writes `labelsSet` directly. `isOn` is per-overlay
// because the conventions differ: the analysis overlay defaults to an explicit
// `false` and the renderer treats anything that is NOT false as on, so `!!value`
// would disagree with the canvas for a preset that omits the key.
export const OVERLAY_DEFS = [
  {
    key: 'showAnalysisOverlay',
    title: 'Pedigree analysis',
    sub: 'Inheritance-pattern and cascade annotations',
    isOn: (ls) => ls?.showAnalysisOverlay !== false,
    // Mirrors the previous quick toggle exactly, including the `=== false` flip.
    toggle: (prev) => ({
      ...prev,
      showAnalysisOverlay: prev?.showAnalysisOverlay === false
    }),
    // Only meaningful once the family has an analysis to annotate.
    available: ({ analysis }) => !!analysis?.family
  }
];

const SECTION_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.disabled',
  mb: 1
};

const RingSwatch = ({ colour, dashed }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: `2px ${dashed ? 'dashed' : 'solid'} ${colour}`,
      flex: '0 0 auto',
      mt: '4px'
    }}
  />
);

/**
 * Why a record-derived row cannot be used right now, in the clinician's words.
 * Never a bare empty result: an overlay that answers "nobody" when it actually
 * means "I was not allowed to look" is the one failure mode a gap-spotting tool
 * must not have.
 */
const rowUnavailableReason = ({
  preset,
  factsLoading,
  factsError,
  unavailable
}) => {
  if (preset.roles !== 'facts') return null;
  if (unavailable?.notes === 'role') {
    return 'Not available for student accounts';
  }
  if (factsError) return 'Records could not be loaded';
  if (factsLoading) return 'Loading records';
  return null;
};

const SlotRow = ({
  slot,
  index,
  preset,
  onToggle,
  onRemove,
  onOpenCriteria,
  disabledReason
}) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
    <RingSwatch colour={slotColour(index)} dashed={index === 1} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14 }} noWrap>
        {preset?.title || slot.presetId}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {disabledReason ||
          (slot.results
            ? [
                `${slot.results.count}`,
                slot.results.hiddenCount
                  ? `${slot.results.hiddenCount} hidden`
                  : null,
                slot.results.unresolvedCount
                  ? `${slot.results.unresolvedCount} unresolved`
                  : null
              ]
                .filter(Boolean)
                .join(' · ')
            : '')}
      </Typography>
    </Box>
    <Switch
      size="small"
      checked={!!slot.enabled && !disabledReason}
      disabled={!!disabledReason}
      onChange={onToggle}
      slotProps={{ input: { 'aria-label': preset?.title || slot.presetId } }}
    />
    <Tooltip title="Edit criteria">
      <span>
        <IconButton
          size="small"
          aria-label={`Edit criteria for ${preset?.title || slot.presetId}`}
          disabled={!!disabledReason}
          onClick={onOpenCriteria}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
    <Tooltip title="Remove">
      <IconButton
        size="small"
        aria-label={`Remove ${preset?.title || slot.presetId}`}
        onClick={onRemove}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const OverlaysMenu = ({
  labelsSet,
  setLabelsSet,
  analysis,
  accent,
  // TGCLOUD-1670 data overlays. Every one of these is optional so the component
  // still renders on a surface that has not wired them.
  slots = [],
  presets = DATA_OVERLAY_PRESETS,
  treatment = 'ring',
  onAddSlot,
  onRemoveSlot,
  onToggleSlot,
  onUpdateSlot,
  onTreatmentChange,
  onOpen,
  // Bumped by the V shortcut. The popover is anchored to this component's own
  // icon, so opening it from outside means signalling rather than passing an
  // anchor element around.
  openSignal = 0,
  facts,
  factsLoading = false,
  factsError = null,
  unavailable = null,
  personIndex,
  asOf,
  fmtDate,
  memberRecords,
  diagnoses
}) => {
  const iconRef = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [addAnchor, setAddAnchor] = React.useState(null);
  const [criteriaFor, setCriteriaFor] = React.useState(null);
  const close = () => setAnchorEl(null);

  const dataOverlaysWired = typeof onAddSlot === 'function';
  const available = OVERLAY_DEFS.filter((o) => o.available({ analysis }));
  const presetOf = React.useCallback(
    (id) => presets.find((p) => p.id === id) || null,
    [presets]
  );

  const activeCount =
    available.filter((o) => o.isOn(labelsSet)).length +
    slots.filter((s) => s?.enabled).length;

  const categories = OVERLAY_CATEGORIES.filter((c) =>
    presets.some((p) => p.category === c)
  );
  const usedIds = new Set(slots.map((s) => s?.presetId));
  const atCapacity = slots.length >= MAX_DATA_OVERLAY_SLOTS;

  React.useEffect(() => {
    if (!openSignal) return;
    setAnchorEl(iconRef.current);
    onOpen?.();
    // onOpen is a stable callback from the hook; re-running on its identity
    // would reopen the popover the user just closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const openPopover = (e) => {
    setAnchorEl(e.currentTarget);
    // Opening the popover is what makes the facts worth fetching: a pedigree
    // nobody overlays never pays for the read.
    onOpen?.();
  };

  return (
    <>
      <Tooltip
        title={
          activeCount
            ? `Overlays (${activeCount} on)`
            : 'Overlays on the pedigree'
        }
      >
        <IconButton
          ref={iconRef}
          size="small"
          data-va-anchor="pedigree.header.overlays"
          onClick={openPopover}
        >
          <Badge
            variant="dot"
            invisible={!activeCount}
            sx={{ '& .MuiBadge-dot': { backgroundColor: accent } }}
          >
            <LayersIcon
              fontSize="small"
              sx={{ color: activeCount ? accent : '#bdbdbd' }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          sx={{ p: 2, width: 340 }}
          data-va-anchor="pedigree.overlays.popover"
        >
          {available.length > 0 && (
            <>
              <Typography sx={SECTION_SX}>Overlays</Typography>
              {available.map((o) => (
                <FormControlLabel
                  key={o.key}
                  control={
                    <Switch
                      size="small"
                      checked={o.isOn(labelsSet)}
                      onChange={() => setLabelsSet(o.toggle)}
                      slotProps={{ input: { 'aria-label': o.title } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 14 }}>{o.title}</Typography>
                      <Typography
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                      >
                        {o.sub}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5, ml: 0 }}
                />
              ))}
              {dataOverlaysWired && <Divider sx={{ my: 1.5 }} />}
            </>
          )}

          {dataOverlaysWired && (
            <>
              <Typography sx={SECTION_SX}>Data overlays</Typography>

              {slots.length === 0 && (
                <Typography
                  sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}
                >
                  Mark the family members who match a record criterion, without
                  changing the pedigree itself.
                </Typography>
              )}

              {slots.map((slot, index) => {
                const preset = presetOf(slot.presetId);
                return (
                  <SlotRow
                    key={`${slot.presetId}-${index}`}
                    slot={slot}
                    index={index}
                    preset={preset}
                    disabledReason={
                      preset
                        ? rowUnavailableReason({
                            preset,
                            factsLoading,
                            factsError,
                            unavailable
                          })
                        : 'Unknown overlay'
                    }
                    onToggle={() => onToggleSlot(index)}
                    onRemove={() => onRemoveSlot(index)}
                    onOpenCriteria={(e) =>
                      setCriteriaFor({ index, anchor: e.currentTarget })
                    }
                  />
                );
              })}

              <Tooltip
                title={
                  atCapacity
                    ? 'Turn one off first (two overlays at a time)'
                    : ''
                }
              >
                <span>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={atCapacity}
                    onClick={(e) => setAddAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none', mt: 0.5 }}
                  >
                    Add overlay
                  </Button>
                </span>
              </Tooltip>

              <Divider sx={{ my: 1.5 }} />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={treatment}
                onChange={(_e, v) => v && onTreatmentChange?.(v)}
                aria-label="Overlay treatment"
                fullWidth
              >
                {TREATMENTS.map((t) => (
                  <ToggleButton
                    key={t.value}
                    value={t.value}
                    sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}
                  >
                    {t.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </>
          )}
        </Box>
      </Popover>

      <Menu
        open={Boolean(addAnchor)}
        anchorEl={addAnchor}
        onClose={() => setAddAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 420, width: 320 } } }}
      >
        {categories.flatMap((category) => [
          <ListSubheader key={`h-${category}`} sx={{ fontSize: 11 }}>
            {category}
          </ListSubheader>,
          ...presets
            .filter((p) => p.category === category)
            .map((p) => {
              const reason = rowUnavailableReason({
                preset: p,
                factsLoading,
                factsError,
                unavailable
              });
              // Loading is not a reason to refuse the click: the row will fill
              // in as soon as the payload lands. A role refusal is.
              const blocked =
                usedIds.has(p.id) ||
                reason === 'Not available for student accounts';
              return (
                <Tooltip
                  key={p.id}
                  title={
                    usedIds.has(p.id)
                      ? 'Already on'
                      : blocked
                      ? reason
                      : p.description
                  }
                  placement="left"
                >
                  <span>
                    <MenuItem
                      disabled={blocked}
                      sx={{ fontSize: 13, whiteSpace: 'normal' }}
                      onClick={() => {
                        onAddSlot(p.id);
                        setAddAnchor(null);
                      }}
                    >
                      {p.title}
                    </MenuItem>
                  </span>
                </Tooltip>
              );
            })
        ])}
      </Menu>

      {criteriaFor != null && (
        <DataOverlayCriteriaPopover
          open
          anchorEl={criteriaFor.anchor}
          onClose={() => setCriteriaFor(null)}
          preset={presetOf(slots[criteriaFor.index]?.presetId)}
          params={slots[criteriaFor.index]?.params}
          slotOptions={slots[criteriaFor.index]?.options}
          facts={facts}
          personIndex={personIndex}
          asOf={asOf}
          fmtDate={fmtDate}
          memberRecords={memberRecords}
          diagnoses={diagnoses}
          accent={accent}
          onApply={(params, options) =>
            onUpdateSlot(criteriaFor.index, { params, options })
          }
        />
      )}
    </>
  );
};

export default OverlaysMenu;
